// src/services/api/itineraryApi.ts
import { Itinerary } from '../dataPersistence/types';
import { authTokenManager } from '../../utils/authTokenManager';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  statusCode?: number;
}

/**
 * API service for communicating with the itinerary endpoints
 */
export class ItineraryApiService {
  private baseUrl: string;
  
  constructor() {
    this.baseUrl = `${import.meta.env.VITE_API_BASE_URL || ''}/api/user-data/itineraries`;
  }
  
  /**
   * Get authentication headers for API requests
   */
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    // Get the current auth token
    const token = await authTokenManager.getValidToken();
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }
  
  /**
   * Get all itineraries for the current user
   */
  public async getAllItineraries(): Promise<ApiResponse<Itinerary[]>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('[ItineraryApiService] Error fetching itineraries:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Get a specific itinerary by ID
   */
  public async getItinerary(id: string): Promise<ApiResponse<Itinerary>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error(`[ItineraryApiService] Error fetching itinerary ${id}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Create a new itinerary on the server
   */
  public async createItinerary(itinerary: Omit<Itinerary, 'userId'>): Promise<ApiResponse<Itinerary>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(itinerary)
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('[ItineraryApiService] Error creating itinerary:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Update an existing itinerary
   */
  public async updateItinerary(id: string, updates: Partial<Itinerary>): Promise<ApiResponse<Itinerary>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error(`[ItineraryApiService] Error updating itinerary ${id}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Delete an itinerary
   */
  public async deleteItinerary(id: string): Promise<ApiResponse<void>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      return { success: true };
    } catch (error) {
      console.error(`[ItineraryApiService] Error deleting itinerary ${id}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Create singleton instance
export const itineraryApiService = new ItineraryApiService();
